import type { AccountUserSettingDto } from '../../types/api';
import {
  SRA_ASSESSMENT_IDS_SETTING,
  isTruthySraSettingValue,
  normalizeSraSettingName,
  parseSraAssessmentIds,
} from './SraMenuAccess';

/**
 * Office Online (SRA AccessType 30) access settings, stored in AccountSraDto.ConfigInfo:
 *
 *   assessmentids       comma-delimited AssessmentIds the user may administer (in practice order)
 *   enableaccesscode    '1'/'0'  — require an access code      (default disabled)
 *   accesscode          the code                               (default '1234')
 *   enablelaunchlist    '1'/'0'  — show the launch list        (default enabled)
 *   enablemainmenu      '1'/'0'  — show the main menu          (default enabled)
 *
 * A setting that is absent means "use the default"; saving writes all of them explicitly.
 * For assessmentids, absent means every practice assessment is enabled.
 */
export const OFFICE_ENABLE_ACCESS_CODE_SETTING = 'enableaccesscode';
export const OFFICE_ACCESS_CODE_SETTING = 'accesscode';
export const OFFICE_ENABLE_LAUNCH_LIST_SETTING = 'enablelaunchlist';
export const OFFICE_ENABLE_MAIN_MENU_SETTING = 'enablemainmenu';
export const OFFICE_DEFAULT_ACCESS_CODE = '1234';

export interface OfficeOnlineDraft {
  /** null = the setting is absent: every practice assessment is enabled. */
  assessmentIds: string[] | null;
  enableAccessCode: boolean;
  accessCode: string;
  enableLaunchList: boolean;
  enableMainMenu: boolean;
}

const findSetting = (
  configInfo: AccountUserSettingDto[] | undefined,
  name: string
): AccountUserSettingDto | undefined => (configInfo ?? []).find(
  (setting) => normalizeSraSettingName(setting.Name || '') === name
);

const readBool = (configInfo: AccountUserSettingDto[] | undefined, name: string, fallback: boolean): boolean => {
  const entry = findSetting(configInfo, name);
  return entry ? isTruthySraSettingValue(entry.Value) : fallback;
};

export const parseOfficeOnlineSettings = (configInfo: AccountUserSettingDto[] | undefined): OfficeOnlineDraft => {
  const hasAssessmentSetting = !!findSetting(configInfo, SRA_ASSESSMENT_IDS_SETTING);
  const accessCodeEntry = findSetting(configInfo, OFFICE_ACCESS_CODE_SETTING);

  return {
    assessmentIds: hasAssessmentSetting ? parseSraAssessmentIds(configInfo) : null,
    enableAccessCode: readBool(configInfo, OFFICE_ENABLE_ACCESS_CODE_SETTING, false),
    accessCode: accessCodeEntry ? (accessCodeEntry.Value ?? '') : OFFICE_DEFAULT_ACCESS_CODE,
    enableLaunchList: readBool(configInfo, OFFICE_ENABLE_LAUNCH_LIST_SETTING, true),
    enableMainMenu: readBool(configInfo, OFFICE_ENABLE_MAIN_MENU_SETTING, true),
  };
};

/**
 * The assessments that are enabled, in `allAssessmentIds` (practice) order. A draft with no
 * explicit list enables them all; ids no longer in the practice list are dropped. When the
 * practice list isn't known (empty), an explicit list is returned as-is.
 */
export const resolveEnabledAssessmentIds = (draft: OfficeOnlineDraft, allAssessmentIds: string[]): string[] => {
  if (draft.assessmentIds === null) {
    return [...allAssessmentIds];
  }
  if (allAssessmentIds.length === 0) {
    return [...draft.assessmentIds];
  }
  const enabled = new Set(draft.assessmentIds);
  return allAssessmentIds.filter((id) => enabled.has(id));
};

const sameIds = (a: string[], b: string[]): boolean => a.length === b.length && a.every((id, index) => id === b[index]);

export const officeOnlineDraftsEqual = (a: OfficeOnlineDraft, b: OfficeOnlineDraft, allAssessmentIds: string[]): boolean =>
  sameIds(resolveEnabledAssessmentIds(a, allAssessmentIds), resolveEnabledAssessmentIds(b, allAssessmentIds))
  && a.enableAccessCode === b.enableAccessCode
  && a.accessCode === b.accessCode
  && a.enableLaunchList === b.enableLaunchList
  && a.enableMainMenu === b.enableMainMenu;

const upsertSetting = (
  configInfo: AccountUserSettingDto[],
  name: string,
  value: string
): AccountUserSettingDto[] => {
  const index = configInfo.findIndex((setting) => normalizeSraSettingName(setting.Name || '') === name);
  if (index < 0) {
    return [...configInfo, { Name: name, Value: value }];
  }
  const next = [...configInfo];
  next[index] = { ...configInfo[index], Value: value };
  return next;
};

/**
 * Writes every Office Online setting into ConfigInfo (other settings are kept untouched). The
 * enabled assessments are compiled into the comma-delimited assessmentids list in practice order.
 * If the draft has no explicit list and the practice list is unknown, assessmentids is left alone.
 */
export const applyOfficeOnlineSettings = (
  configInfo: AccountUserSettingDto[] | undefined,
  draft: OfficeOnlineDraft,
  allAssessmentIds: string[]
): AccountUserSettingDto[] => {
  let next = [...(configInfo ?? [])];

  if (draft.assessmentIds !== null || allAssessmentIds.length > 0) {
    next = upsertSetting(next, SRA_ASSESSMENT_IDS_SETTING, resolveEnabledAssessmentIds(draft, allAssessmentIds).join(','));
  }
  next = upsertSetting(next, OFFICE_ENABLE_ACCESS_CODE_SETTING, draft.enableAccessCode ? '1' : '0');
  next = upsertSetting(next, OFFICE_ACCESS_CODE_SETTING, draft.accessCode);
  next = upsertSetting(next, OFFICE_ENABLE_LAUNCH_LIST_SETTING, draft.enableLaunchList ? '1' : '0');
  next = upsertSetting(next, OFFICE_ENABLE_MAIN_MENU_SETTING, draft.enableMainMenu ? '1' : '0');

  return next;
};
